import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export interface AmountAdjustmentRule {
    amount: number;
    tiers: { min_amount: number; max_amount: number | null; amount: number }[];
}
export type AmountAdjustmentRules = Record<string, AmountAdjustmentRule>;

export const validateAmountAdjustments = (rules: AmountAdjustmentRules): string | null => {
    for (const [method, rule] of Object.entries(rules)) {
        if (!Number.isFinite(rule.amount) || rule.amount < 0) return `${method}: укажите неотрицательную прибавку`;
        const ordered = [...rule.tiers].sort((a, b) => a.min_amount - b.min_amount);
        for (let i = 0; i < ordered.length; i++) {
            const tier = ordered[i];
            if (![tier.min_amount, tier.amount].every(v => Number.isFinite(v) && v >= 0)
                || (tier.max_amount !== null && (!Number.isFinite(tier.max_amount) || tier.max_amount <= tier.min_amount))) {
                return `${method}: проверьте границы диапазона и прибавку`;
            }
            if (i && (ordered[i - 1].max_amount === null || tier.min_amount < ordered[i - 1].max_amount!)) {
                return `${method}: диапазоны прибавки пересекаются`;
            }
        }
    }
    return null;
};

export const MerchantAmountAdjustments: React.FC<{
    methods: string[];
    value: AmountAdjustmentRules;
    onChange: (value: AmountAdjustmentRules) => void;
}> = ({ methods, value, onChange }) => {
    const error = validateAmountAdjustments(value);
    return <div className="mt-6 space-y-4 border-t pt-6">
        <details>
        <summary className="cursor-pointer font-medium">Прибавка к сумме мерчанта (KZT)</summary>
        <div className="mt-4 space-y-4">
        <p className="text-sm text-slate-500">Клиент и провайдер получают исходную сумму. Прибавка увеличивает сумму мерчанта, базу его комиссии и зачисление. Общая для FTD, STD и trusted; методы with_comission работают без прибавки. Изменения действуют только для новых платежей.</p>
        {methods.map(method => {
            const rule = value[method] || { amount: 0, tiers: [] };
            const update = (next: AmountAdjustmentRule) => onChange({ ...value, [method]: next });
            return <div key={method} className="rounded border p-3 space-y-3">
                <div className="font-mono text-sm">{method}</div>
                <Label>Базовая прибавка, KZT
                    <Input aria-label={`${method}: базовая прибавка`} type="number" min="0" step="any" value={Number.isNaN(rule.amount) ? '' : rule.amount}
                        onChange={e => update({ ...rule, amount: e.target.valueAsNumber })} />
                </Label>
                {rule.tiers.map((tier, index) => {
                    const change = (key: keyof typeof tier, amount: number | null) => update({
                        ...rule, tiers: rule.tiers.map((item, i) => i === index ? { ...item, [key]: amount } : item),
                    });
                    return <div key={index} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                        <Label>От (включительно)<Input type="number" min="0" step="any" value={Number.isNaN(tier.min_amount) ? '' : tier.min_amount} onChange={e => change('min_amount', e.target.valueAsNumber)} /></Label>
                        <Label>До (не включая)<Input type="number" min="0" step="any" placeholder="Без ограничения" value={tier.max_amount ?? ''} onChange={e => change('max_amount', e.target.value === '' ? null : e.target.valueAsNumber)} /></Label>
                        <Label>Прибавка, KZT<Input type="number" min="0" step="any" value={Number.isNaN(tier.amount) ? '' : tier.amount} onChange={e => change('amount', e.target.valueAsNumber)} /></Label>
                        <Button type="button" variant="outline" onClick={() => update({ ...rule, tiers: rule.tiers.filter((_, i) => i !== index) })}>Удалить диапазон</Button>
                    </div>;
                })}
                <Button type="button" variant="outline" onClick={() => update({ ...rule, tiers: [...rule.tiers, { min_amount: rule.tiers[rule.tiers.length - 1]?.max_amount ?? 0, max_amount: null, amount: rule.amount }] })}>Добавить диапазон</Button>
                <p className="text-xs text-slate-500">Диапазоны определяются по фактической сумме оплаты. Вне диапазонов применяется базовая прибавка.</p>
            </div>;
        })}
        </div>
        </details>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>;
};
